import React, { useState, useEffect } from "react";
import { X, Sparkles, User, Bot, Calendar, Brain } from "lucide-react";
import { api, API_BASE } from "./api_service/api";

interface AnalysisResult {
  verdict: string;
  source: string;
  human_percentage: number;
  ai_percentage: number;
  analysed_at: string;
  detection_results?: any;
}

interface ResultsModalProps {
  result: AnalysisResult;
  onClose: () => void;
}

const ResultsModal: React.FC<ResultsModalProps> = ({ result, onClose }) => {
  const [explanation, setExplanation] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!result?.detection_results) return;

    let abortController = new AbortController();

    const startReasoning = async () => {
      setIsLoading(true);
      setError(null);
      setExplanation("");

      try {
        if (!api.isAuthenticated) {
          await api.login();
        }

        const apiKey = (api as any).apiKey;
        const token = (api as any).token;

        if (!apiKey || !token) {
          throw new Error("Authentication failed");
        }

        console.log("🚀 Starting stream...");

        const response = await fetch(`${API_BASE}/reasoning/stream-text`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": apiKey,
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ detection_results: result.detection_results }),
          signal: abortController.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        if (!response.body) {
          throw new Error("No response body");
        }

        console.log("✅ Stream started");

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            console.log("✅ Stream complete");
            setIsLoading(false);
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";

          for (const line of lines) {
            if (!line.trim() || !line.startsWith("data: ")) continue;

            try {
              const data = JSON.parse(line.substring(6));
              console.log("📨 Received:", data.type);

              if (data.type === "start") {
                setExplanation(data.content || "");
              } else if (data.type === "chunk") {
                setExplanation((prev) => prev + (data.content || ""));
              } else if (data.type === "done") {
                setIsLoading(false);
                return;
              } else if (data.type === "error") {
                setError(data.content || "An error occurred");
                setIsLoading(false);
                return;
              }
            } catch (e) {
              console.error("Parse error:", e, line);
            }
          }
        }
      } catch (err: any) {
        if (err.name === "AbortError") {
          console.log("Stream aborted");
          return;
        }
        console.error("❌ Error:", err);
        setError(err.message || "Failed to generate reasoning");
        setIsLoading(false);
      }
    };

    startReasoning();

    return () => {
      console.log("🔌 Aborting stream");
      abortController.abort();
    };
  }, [result?.detection_results]);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[95vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-neutral-200">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-neutral-900" />
            <h3 className="text-lg font-semibold">Analysis Complete</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-neutral-100"
          >
            <X className="w-5 h-5 text-neutral-500" />
          </button>
        </div>

        <div className="p-6 space-y-8">
          <div className="text-center">
            <p className="text-xs uppercase tracking-wider text-neutral-500 mb-2">
              Verdict
            </p>
            <p className="text-2xl font-semibold">{result.verdict}</p>
          </div>

          <div className="text-center">
            <p className="text-xs uppercase tracking-wider text-neutral-500 mb-2">
              Source
            </p>
            <p className="text-2xl font-semibold">{result.source}</p>
          </div>

          <div className="flex gap-3">
            <div className="flex-1 p-4 rounded-xl border border-neutral-200">
              <div className="flex items-center gap-2 mb-2">
                <User className="w-4 h-4 text-neutral-500" />
                <span className="text-xs font-medium text-neutral-500">
                  Human
                </span>
              </div>
              <p className="text-2xl font-semibold">
                {Math.round(result.human_percentage)}%
              </p>
            </div>
            <div className="flex-1 p-4 rounded-xl border border-neutral-200">
              <div className="flex items-center gap-2 mb-2">
                <Bot className="w-4 h-4 text-neutral-500" />
                <span className="text-xs font-medium text-neutral-500">AI</span>
              </div>
              <p className="text-2xl font-semibold">
                {Math.round(result.ai_percentage)}%
              </p>
            </div>
          </div>

          <div className="p-6 rounded-2xl border border-neutral-200 bg-neutral-50">
            <div className="flex items-center gap-3 mb-6">
              <Brain className="w-6 h-6 text-indigo-600" />
              <h4 className="text-lg font-semibold">Detailed Reasoning</h4>
              {isLoading && (
                <div className="ml-auto">
                  <div className="animate-spin h-5 w-5 border-2 border-indigo-600 border-t-transparent rounded-full" />
                </div>
              )}
            </div>
            <div className="min-h-[100px]">
              {error ? (
                <span className="text-red-600">{error}</span>
              ) : explanation ? (
                <pre className="whitespace-pre-wrap font-sans text-neutral-700">
                  {explanation}
                </pre>
              ) : (
                <span className="text-neutral-500 italic">
                  {isLoading
                    ? "Connecting and generating reasoning..."
                    : "No reasoning available"}
                </span>
              )}
            </div>
          </div>

          <div className="flex justify-between text-sm py-3 border-t border-neutral-200">
            <div className="flex items-center gap-2 text-neutral-500">
              <Calendar className="w-4 h-4" />
              <span>Analyzed</span>
            </div>
            <span>
              {new Date(result.analysed_at).toLocaleString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
          </div>

          <button
            onClick={onClose}
            className="w-full bg-indigo-600 text-white font-medium py-3 rounded-xl hover:bg-indigo-700"
          >
            Analyze Another
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResultsModal;
